import { memo } from "react";
import type { NormValue, TradeFields } from "../../helpers/extractTrade";
import CardSourceToggle from "./CardSourceToggle";
import { ActionBadge, CardHeader, CardShell, FieldList, displayValue } from "./trade";

type Normalized = Record<string, { raw: string; value: NormValue }>;

type QualitativeTradeCardProps = {
  ticker: string;
  fields: TradeFields;
  normalized: Normalized;
  raw?: string;
  companyName?: string;
};

// row order — only rows whose field is present render
const ROWS: Array<{ key: string; label: string }> = [
  { key: "strategy", label: "Strategy" },
  { key: "strike", label: "Strike" },
  { key: "expiry", label: "Expiry" },
  { key: "current_price", label: "Current price" },
  { key: "entry_ask", label: "Entry / Ask" },
  { key: "breakeven", label: "Break-even" },
  { key: "max_loss", label: "Max loss" },
  { key: "max_gain", label: "Max gain" },
  { key: "pop", label: "Prob. ITM" },
  { key: "iv", label: "Implied Vol" },
];

const QualitativeTradeCard = ({ ticker, fields, normalized: N, raw, companyName }: QualitativeTradeCardProps) => {
  const signal = (N.signal?.value as string | undefined) ?? undefined;
  const bullish = signal?.toLowerCase() === "bullish";
  const bearish = signal?.toLowerCase() === "bearish";

  const verb = N.buy_sell?.value ? String(N.buy_sell.value) : undefined;
  const instrument = N.call_put?.value ? String(N.call_put.value) : undefined;

  const rows = ROWS
    .map(r => ({ label: r.label, value: displayValue(N[r.key]) }))
    .filter((r): r is { label: string; value: string } => !!r.value);

  const rationale = fields.rationale;

  return (
    <CardShell>
      <CardHeader
        ticker={ticker}
        companyName={companyName}
        signal={signal ? { label: signal, tone: bullish ? "bull" : bearish ? "bear" : "neutral" } : null}
      />

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <ActionBadge verb={verb} instrument={instrument} />
        <span className="text-xs italic text-token-text-secondary">Indicative idea · no fixed contract</span>
      </div>

      <FieldList rows={rows} />

      {rationale && (
        <p className="mt-3 text-sm leading-relaxed text-token-text-secondary">{rationale}</p>
      )}

      <CardSourceToggle raw={raw} />
    </CardShell>
  );
};

const propsAreEqual = (prev: QualitativeTradeCardProps, next: QualitativeTradeCardProps) =>
  prev.raw != null
  && prev.raw === next.raw
  && prev.ticker === next.ticker
  && prev.companyName === next.companyName;

export default memo(QualitativeTradeCard, propsAreEqual);
